import { useState, useEffect, useMemo } from 'react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAPICall } from '../../../../../hooks';
import type { AttachmentImageBA } from '../../../../../sdk/type';
import { getRoundedNumber } from '../../../../../ui/utils/common';
import { LoggerService, dateTimeToCustomFormat, formatDateDMYYYY } from '../../../../../ui/utils';
import ParticipantService from '../../../../../sdk/services/participantService';
import PinService from '../../../../../sdk/services/pinService';
import RewardService from '../../../../../sdk/services/rewardService';
import PhotoUnavailable from '../../../../../assets/image/sample/photoUnavailable.png';
import Personalize from '../../../../../assets/image/personalize.png';
import {
  AdminPageHeader,
  ButtonType,
  Card,
  HeaderStyle,
  IconButtonProps,
  IconStyle,
  ImageSlider,
  Theme,
  ProfileImage,
  TableContainer,
  Tag,
  TagStyle,
} from '../../../../../ui/components';
import { useAdminToast } from '../../../../../ui/components/AdminToast/AdminToast';
import { decryptString, encryptString } from '../../../../../ui/utils/cryptoUtils';
//import { HeaderFlex } from '../../../../../ui/components/Form/Card/types/enums';
import { DynamicObjectInterface } from '../../../../../sdk/type';
import AttachmentService from '../../../../../sdk/services/attachmentService';
import healthGoalService from '../../../../../sdk/services/healthGoalService';
import AuditLogService from '../../../../../sdk/services/auditLogService';
import AuditHistoryAccordion from '../../../audit-log/auditLogAccordion';
import { DetailsModal, HealthGoalModal, PointAllocationModal, ResultModal, RewardGiftModal } from './editModal';
import getRewardListConfig from './participantDetailsConfig';
import styles from './index.module.scss';

export interface ParticipantData {
  recId?: string;
  campaign: {
    campaignName: string;
    campaignStart: string;
    campaignEnd: string;
    campaignId: string;
  };
  userId: string;
  coachID: string;
  coachName: string;
  enrollmentStatus?: string;
  email: string;
  isNewAbo: number;
  loginId: string;
  name: string;
  phone: string;
  pinLevel: string;
  memberType: string;
  point: PointData;
  purchase: {
    recId?: number;
    orderPoint?: number;
    orderNumber?: string;
    orderName?: string;
    orderStatus?: number;
  }[];
  remark: string;
  campaignStatRecId?: number;
  rewards: {
    rewardRemarks?: string;
    rewardId?: number;
    rewardCode?: string;
    rewardName?: string;
    rewardType?: string;
    isNew?: boolean;
    redeemDate?: string;
    rewardRecId: string;
    rewardStatus?: string;
  }[];
  status: string;
  subscribe: {
    recId?: number;
    orderPoint?: number;
    orderNumber?: string;
    orderStatus?: number;
  }[];
  team: {
    teamId: string;
    teamName: string;
    teamStartDate: string;
    teamEndDate: string;
    teamIsIndividual?: number;
    teamLeaderLoginId: string;
    teamLeaderName: string;
  };
  weightIn: {
    acknowledgeBy?: string;
    acknowledgeByName?: string;
    acknowledgeDateTime?: string;
    bodyFat: number;
    height: number;
    machine: string;
    muscleKilogram: number;
    musclePercentage: number;
    weightedDate: string;
    weight: number;
    remarks: string;
  };
  weightOut: {
    acknowledgeBy?: string;
    acknowledgeByName?: string;
    acknowledgeDateTime?: string;
    bodyFat: number;
    height: number;
    machine: string;
    muscleKilogram: number;
    musclePercentage: number;
    weightedDate: string;
    weight: number;
    remarks: string;
  };
  weightOutDiffValues: {
    bodyFatDiff?: number;
    muscleKilogramDiff?: number;
    musclePercentageDiff?: number;
    weightDiff?: number;
    winBodyFat?: boolean;
    winMuscle?: boolean;
    winWeight?: boolean;
    result?: boolean;
  };
  isIndividualAchieved?: number;
  isIndividualCompleted?: number;
}

export interface PointData {
  purchase: string;
  purchaseAdj: string;
  subscribe: string;
  subscribeAdj: string;
  winning: string;
  winningAdj: string;
  remarks: string;
}

export interface MasterData {
  pin: {
    pinId: number;
    pinName: string;
  }[];
  reward: {
    recId: number;
    rewardCode: string;
    rewardName: string;
  }[];
}

export interface SearchParams {
  campaignId: string;
  teamId: string;
  userId: string;
}

export interface UserAnswerDataType {
  answerId: number;
  questionType: string;
  answerText: string;
  options: DynamicObjectInterface[] | null;
}

export interface CampaignQuestionType {
  questionRecId: number;
  questionText: string;
  questionType: string;
  options: null;
}

type SectionState = {
  // eslint-disable-next-line no-unused-vars
  [key in (typeof Sections)[keyof typeof Sections]]: boolean;
};

const Sections = {
  ParticipantDetails: 'participantDetails',
  HealthGoal: 'healthGoal',
  PointAllocation: 'pointAllocation',
  RewardGift: 'rewardGift',
  Result: 'weightDetails',
} as const;

export const AdminParticipantDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();

  const [data, setData] = useState<ParticipantData | null>(null);
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  const [showEdit, setShowEdit] = useState<SectionState>(
    Object.fromEntries(Object.values(Sections).map(section => [section, false])) as SectionState,
  );
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [isLoading, setIsLoading] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>('');

  const [healthGoalData, setHealthGoalData] = useState('');
  const [userAnswerData, setUserAnswerData] = useState<UserAnswerDataType>();
  //const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [healthQuestionData, setHealthQuestionData] = useState<CampaignQuestionType[]>([]);
  const [auditHistoryLog, setAuditHistoryLog] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let keys: SearchParams = {
      campaignId: params.get('campaignId') ?? '',
      teamId: params.get('teamId') ?? '',
      userId: params.get('userId') ?? '',
    };
    setSearchParams(keys);
  }, [location]);

  useEffect(() => {
    if (searchParams) {
      fetchParticipantDetails();
      fetchMasterData();
      fetchProfileImage();
      fetchMeasurementImage();
      fetchHealthGoalAnswer();
      fetchHealthGoalQuestion();
      fetchAuditLogs();
    }
  }, [searchParams]);

  const fetchAuditLogs = async () => {
    try {
      if (searchParams?.campaignId && searchParams?.teamId && searchParams?.userId) {
        const payload = {
          campaignId: searchParams?.campaignId,
          teamId: searchParams?.teamId,
          userId: searchParams?.userId,
        };

        const historyLogResponse = await apiCall(AuditLogService.fetchLog, payload);
        const historyLogResult = historyLogResponse.data;

        setAuditHistoryLog(historyLogResult.data || []);
      }
    } catch (error) {
      LoggerService.log('Failed to retrieve audit history log', error);
    }
  };

  const fetchProfileImage = async () => {
    try {
      const response = await AttachmentService.downloadProfileImage(searchParams?.userId ?? '');
      if (response.data && response.data.data && response.data.data.image) {
        setProfileImageUrl(response.data.data.image);
      }
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  const [images, setImages] = useState<Record<string, AttachmentImageBA>>({
    before_front: { photoId: 0, url: '' },
    after_front: { photoId: 0, url: '' },
    before_side: { photoId: 0, url: '' },
    after_side: { photoId: 0, url: '' },
  });

  const fetchMeasurementImage = async () => {
    if (searchParams) {
      setIsLoading(true);
      try {
        const payload = {
          campaignId: searchParams?.campaignId,
          teamId: searchParams?.teamId,
          loginId: searchParams?.userId,
        };
        const response = await AttachmentService.downloadBeforeAfter(payload);

        const imageData = response?.data?.data?.image || [];

        const updatedImages = { ...images };

        imageData.forEach((image: AttachmentImageBA) => {
          const { photoId, photoType, url } = image;
          if (photoType && Object.prototype.hasOwnProperty.call(updatedImages, photoType)) {
            updatedImages[photoType].url = url;
            updatedImages[photoType].photoId = photoId;
          }
        });

        setImages(updatedImages);
      } catch (error) {
        LoggerService.log('Error downloading image', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchHealthGoalAnswer = async () => {
    try {
      const payload = {
        userId: searchParams?.userId,
        campaignId: searchParams?.campaignId,
      };
      const response = await healthGoalService.userAnswer(payload);
      if (response.status === 200) {
        setUserAnswerData(response.data.data);
        if (response.data.data !== null) {
          setHealthGoalData(response.data.data.answerText);
          //setIsUpdateMode(true);
        }
      }
    } catch (error) {
      LoggerService.log('Error retrieving question', error);
    }
  };

  const fetchHealthGoalQuestion = async () => {
    try {
      const payload = {
        campaignId: searchParams?.campaignId,
      };
      const response = await healthGoalService.campaignQuestion(payload);
      setHealthQuestionData(response.data.data);
    } catch (error) {
      LoggerService.log('Error retrieving question', error);
    }
  };

  const fetchParticipantDetails = async () => {
    if (searchParams) {
      setIsLoading(true);
      const result = await apiCall(
        ParticipantService.getParticipantDetail,
        { campaignId: searchParams.campaignId, teamId: searchParams.teamId },
        searchParams.userId ?? '',
      );
      if (result.status === 200) {
        const rawData = result.data.data;
        console.log(rawData);

        const modifiedData: ParticipantData = {
          ...rawData,
          weightIn: {
            ...rawData.weightIn,
            bodyFat: rawData?.weightIn?.bodyFat
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightIn?.bodyFat)))
              : null,
            height: rawData?.weightIn?.height
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightIn?.height)))
              : null,
            muscleKilogram: rawData?.weightIn?.muscleKilogram
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightIn?.muscleKilogram)))
              : null,
            musclePercentage: rawData?.weightIn?.musclePercentage
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightIn?.musclePercentage)))
              : null,
            weight: rawData?.weightIn?.weight
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightIn?.weight)))
              : null,
          },
          weightOut: {
            ...rawData.weightOut,
            bodyFat: rawData?.weightOut?.bodyFat
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightOut?.bodyFat)))
              : null,
            height: rawData?.weightOut?.height
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightOut?.height)))
              : null,
            muscleKilogram: rawData?.weightOut?.muscleKilogram
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightOut?.muscleKilogram)))
              : null,
            musclePercentage: rawData?.weightOut?.musclePercentage
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightOut?.musclePercentage)))
              : null,
            weight: rawData?.weightOut?.weight
              ? getRoundedNumber(parseFloat(decryptString(rawData?.weightOut?.weight)))
              : null,
          },
          // weightOutDiffValues: {
          //   ...rawData.weightOutDiffValues,
          //   bodyFatDiff: rawData?.weightOutDiffValues.bodyFatDiff
          //     ? parseFloat(decryptString(rawData?.weightOutDiffValues.bodyFatDiff))
          //     : null,
          //   muscleKilogramDiff: rawData?.weightOutDiffValues.muscleKilogramDiff
          //     ? parseFloat(decryptString(rawData?.weightOutDiffValues.muscleKilogramDiff))
          //     : null,
          //   musclePercentageDiff: rawData?.weightOutDiffValues.musclePercentageDiff
          //     ? parseFloat(decryptString(rawData?.weightOutDiffValues.musclePercentageDiff))
          //     : null,
          //   weightDiff: rawData?.weightOutDiffValues.weightDiff
          //     ? parseFloat(decryptString(rawData?.weightOutDiffValues.weightDiff))
          //     : null,
          // },
        };
        setData(modifiedData);
      } else {
        setData(null);
      }
      setIsLoading(false);
    }
  };

  const fetchMasterData = async () => {
    let list = {
      pin: {
        endpoint: PinService.getPinList,
      },
      reward: {
        endpoint: RewardService.getRewardList,
      },
    };
    try {
      const results = await Promise.all(Object.values(list).map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data;
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve coach list', error);
    }
  };

  const onBackButtonClick = () => {
    navigate(-1);
  };

  const toggleEditModal = (section: (typeof Sections)[keyof typeof Sections]) => {
    setShowEdit(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const onSaveParticipantDetails = async (formData: ParticipantData, profileImageUrl: string | undefined) => {
    const activeSection = Object.entries(showEdit)?.find(entry => entry[1] === true)?.[0] as
      | (typeof Sections)[keyof typeof Sections]
      | undefined;

    if (activeSection === 'pointAllocation') {
      let ids: (number | undefined)[] = [];
      const editProductIds = new Set(formData.purchase.map(p => p.recId));
      const editSubsribeIds = new Set(formData.subscribe.map(p => p.recId));
      const productIds = data?.purchase.filter(p => !editProductIds.has(p.recId)).map(p => p.recId);
      const subscribeIds = data?.subscribe.filter(p => !editSubsribeIds.has(p.recId)).map(p => p.recId);
      if (productIds?.length) ids = [...productIds];
      if (subscribeIds?.length) ids = [...ids, ...subscribeIds];
      if (ids?.length) await apiCall(ParticipantService.deletePurchaseProduct, ids as number[], searchParams?.userId!);
    }

    const updatedFormData = {
      ...formData,
      pinLevel: masterData?.pin?.find(p => p.pinName === formData.pinLevel)?.pinId,
      rewards: formData?.rewards
        ?.map((reward: DynamicObjectInterface) => {
          reward.rewardRecId = reward.rewardRecId?.toString()?.includes('NEW') ? null : reward.rewardRecId;
          return { ...reward };
        })
        .filter(reward => !(!reward.rewardRecId && reward.rewardStatus === 'REMOVED')),
      weightIn: {
        ...formData.weightIn,
        weightedDate: formData.weightIn.weightedDate
          ? dateTimeToCustomFormat(formData.weightIn.weightedDate?.toString(), 'YYYY-MM-DD')
          : null,
        acknowledgeDateTime: formData.weightIn.acknowledgeDateTime
          ? dateTimeToCustomFormat(formData.weightIn.acknowledgeDateTime?.toString(), 'YYYY-MM-DD')
          : null,
        bodyFat: formData.weightIn.bodyFat ? encryptString(formData.weightIn.bodyFat.toString()) : null,
        height: formData.weightIn.height ? encryptString(formData.weightIn.height.toString()) : null,
        muscleKilogram: formData.weightIn.muscleKilogram
          ? encryptString(formData.weightIn.muscleKilogram.toString())
          : null,
        musclePercentage: formData.weightIn.musclePercentage
          ? encryptString(formData.weightIn.musclePercentage.toString())
          : null,
        weight: formData.weightIn.weight ? encryptString(formData.weightIn.weight.toString()) : null,
      },
      weightOut: {
        ...formData.weightOut,
        weightedDate: formData.weightOut.weightedDate
          ? dateTimeToCustomFormat(formData.weightOut.weightedDate?.toString(), 'YYYY-MM-DD')
          : null,
        acknowledgeDateTime: formData.weightOut.acknowledgeDateTime
          ? dateTimeToCustomFormat(formData.weightOut.acknowledgeDateTime?.toString(), 'YYYY-MM-DD')
          : null,
        bodyFat: formData.weightOut.bodyFat ? encryptString(formData.weightOut.bodyFat.toString()) : null,
        height: formData.weightOut.height ? encryptString(formData.weightOut.height.toString()) : null,
        muscleKilogram: formData.weightOut.muscleKilogram
          ? encryptString(formData.weightOut.muscleKilogram.toString())
          : null,
        musclePercentage: formData.weightOut.musclePercentage
          ? encryptString(formData.weightOut.musclePercentage.toString())
          : null,
        weight: formData.weightOut.weight ? encryptString(formData.weightOut.weight.toString()) : null,
      },
      // weightOutDiffValues: {
      //   ...formData.weightOutDiffValues,
      //   bodyFatDiff: formData.weightOutDiffValues.bodyFatDiff
      //     ? encryptString(formData.weightOutDiffValues.bodyFatDiff.toString())
      //     : null,
      //   muscleKilogramDiff: formData.weightOutDiffValues.muscleKilogramDiff
      //     ? encryptString(formData.weightOutDiffValues.muscleKilogramDiff.toString())
      //     : null,
      //   musclePercentageDiff: formData.weightOutDiffValues.musclePercentageDiff
      //     ? encryptString(formData.weightOutDiffValues.musclePercentageDiff.toString())
      //     : null,
      //   weightDiff: formData.weightOutDiffValues.weightDiff
      //     ? encryptString(formData.weightOutDiffValues.weightDiff.toString())
      //     : null,
      // },
      allocation: {
        purchase: {
          products: [...formData.purchase],
          point: parseFloat(formData.point.purchase),
          pointAdj: parseFloat(formData.point.purchaseAdj),
        },
        alp: {
          invoices: [...formData.subscribe],
          point: parseFloat(formData.point.subscribe),
          pointAdj: parseFloat(formData.point.subscribeAdj),
        },
        winningCriteria: {
          point: parseFloat(formData.point.winning),
          pointAdj: parseFloat(formData.point.winningAdj),
        },
        remarks: formData.point.remarks,
      },
    };
    setProfileImageUrl(profileImageUrl);
    const result = await apiCall(
      ParticipantService.updateParticipantDetails,
      {
        ...updatedFormData,
        campaignId: searchParams?.campaignId,
        teamId: searchParams?.teamId,
        section: activeSection,
      },
      searchParams?.userId!,
    );

    if (result.status === 200) {
      if (!result.data.status) {
        adminToast({
          message: result?.data?.message || t('admin.participant.updateFail'),
          theme: Theme.ERROR,
        });
      } else {
        adminToast({
          message: t('admin.participant.updateSuccess'),
          theme: Theme.SUCCESS,
        });
        await fetchParticipantDetails();
        await fetchMeasurementImage();
        await fetchProfileImage();
      }

      toggleEditModal(activeSection as (typeof Sections)[keyof typeof Sections]);
    } else {
      adminToast({
        message: t('admin.participant.updateFail'),
        theme: Theme.ERROR,
      });
    }
  };

  const onSaveHealthGoal = async (healthGoalData: string | undefined) => {
    const selectedQuestion = healthQuestionData ? healthQuestionData[0] : null;
    try {
      const payload = {
        userId: searchParams?.userId,
        faqId: selectedQuestion?.questionRecId || 1,
        answerText: healthGoalData,
        options: selectedQuestion?.options || null,
        campaignId: searchParams?.campaignId,
        recId: userAnswerData?.answerId || null,
      };
      const response = await healthGoalService.updateHealthGoal(payload);
      if (response.status === 200) {
        adminToast({
          message: t('admin.participant.updateSuccess'),
          theme: Theme.SUCCESS,
        });
        //
        await fetchHealthGoalAnswer();
        toggleEditModal(Sections.HealthGoal);
      } else {
        adminToast({
          message: t('admin.participant.updateFail'),
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      LoggerService.log('Error uploading data', error);
    }
  };

  const getButtonConfig = useMemo(() => {
    let commonConfig = {
      iconName: 'pencil' as IconName,
      iconStyle: IconStyle.SOLID,
      theme: Theme.WHITE,
      title: t('admin.participant.edit'),
    };
    return {
      participantDetails: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.ParticipantDetails) }],
      healthGoal: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.HealthGoal) }],
      pointAllocation: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.PointAllocation) }],
      rewardGift: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.RewardGift) }],
      result: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.Result) }],
    } as { [index: string]: IconButtonProps[] };
  }, []);

  const showModal = (
    <>
      {showEdit.participantDetails && (
        <DetailsModal
          data={data}
          masterData={masterData}
          searchParams={searchParams}
          initialProfileImageUrl={profileImageUrl}
          onSave={onSaveParticipantDetails}
          onCancel={() => toggleEditModal(Sections.ParticipantDetails)}
        />
      )}
      {showEdit.healthGoal && (
        <HealthGoalModal
          data={data}
          onSave={onSaveHealthGoal}
          initialHealthGoal={healthGoalData}
          onCancel={() => toggleEditModal(Sections.HealthGoal)}
        />
      )}
      {showEdit.pointAllocation && (
        <PointAllocationModal
          data={data}
          onSave={onSaveParticipantDetails}
          onCancel={() => toggleEditModal(Sections.PointAllocation)}
        />
      )}
      {showEdit.rewardGift && (
        <RewardGiftModal
          data={data}
          onSave={onSaveParticipantDetails}
          onCancel={() => toggleEditModal(Sections.RewardGift)}
          masterData={masterData}
        />
      )}
      {showEdit.weightDetails && (
        <ResultModal
          data={data}
          searchParams={searchParams}
          onSave={onSaveParticipantDetails}
          onCancel={() => toggleEditModal(Sections.Result)}
          masterData={masterData}
          measurementImages={images}
        />
      )}
    </>
  );

  return (
    <div className={styles['parti']}>
      <AdminPageHeader withBackButton onBackButtonClick={onBackButtonClick} title={t('admin.participant.title')} />
      <div className={styles['parti-spacing-v']} style={{ marginTop: '0.5rem' }}>
        <Card
          title={t('admin.participant.detailsTitle')}
          headerStyle={HeaderStyle.HEADER_5}
          buttonType={ButtonType.ICON}
          button={getButtonConfig.participantDetails}
        >
          <div className={styles['details-top']}>
            <div className={styles['details-top-profile-img']}>
              <ProfileImage
                placeholder={data?.name ?? ''}
                src={profileImageUrl ? profileImageUrl : Personalize}
                isLoading={isLoading}
              />
            </div>
            <div className={styles['details-top-info']}>
              <h3 className={styles['right-offset']}>
                {t('admin.participant.campaign')}: {data?.campaign?.campaignName ?? '-'}
              </h3>
              <div className={styles['details-top-background']}>
                <div className={styles['layout'] + ' ' + styles['right-offset']}>
                  <div className={styles['details-top-info-items']}>
                    <div className={styles['label']}>{t('admin.participant.aboIdEnrollName')}</div>
                    <p>
                      {!!data?.loginId && !!data?.name
                        ? `${data.loginId} / ${data.name} ${data.memberType ? `(${data.memberType})` : ''}`
                        : '-'}
                    </p>
                  </div>
                  <div className={styles['details-top-info-items']}>
                    <div className={styles['label']}>{t('admin.participant.pinLevel')}</div>
                    <p>{data?.pinLevel ?? '-'}</p>
                  </div>
                  <div className={styles['details-top-info-items']}>
                    <div className={styles['label']}>{t('admin.participant.status')}</div>
                    <p>{data?.enrollmentStatus ?? '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles['details-bottom']}>
            <div className={styles['details-bottom-info']}>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.email')}</div>
                <p>{data?.email ?? '-'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.teamName')}</div>
                <p>{data?.team?.teamName ?? '-'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.teamCoach')}</div>
                <p>{!!data?.coachID && !!data?.coachName ? `${data.coachID} - ${data.coachName}` : '-'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.teams.isIndividualTeam')}</div>
                <p>{data?.team?.teamIsIndividual === 1 ? t('admin.participant.individual') : t('admin.participant.team') ?? '-'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.remarks')}</div>
                <p>{data?.remark ?? '-'}</p>
              </div>
            </div>
            <div className={styles['details-bottom-info']}>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.phoneNumber')}</div>
                <p>{data?.phone ?? '-'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.teamPeriod')}</div>
                <p>
                  {!!data?.team?.teamStartDate && !!data?.team?.teamEndDate
                    ? `${formatDateDMYYYY(new Date(data?.team?.teamStartDate))} - ${formatDateDMYYYY(
                        new Date(data?.team?.teamEndDate),
                      )}`
                    : '-'}
                </p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.teamLeader')}</div>
                <p>
                  {!!data?.team?.teamLeaderLoginId && !!data?.team?.teamLeaderName
                    ? `${data?.team?.teamLeaderLoginId} - ${data?.team?.teamLeaderName}`
                    : '-'}
                </p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.isNewAbo')}</div>
                <p>{data?.isNewAbo === undefined ? '-' : data?.isNewAbo ? 'YES' : 'NO'}</p>
              </div>
            </div>
          </div>
        </Card>
        <Card
          title={t('admin.participant.healthGoal')}
          headerStyle={HeaderStyle.HEADER_5}
          buttonType={ButtonType.ICON}
          button={getButtonConfig.healthGoal}
        >
          <div className={styles['details-top-info-items']}>
            <div>
              <p>{healthGoalData}</p>
            </div>
          </div>
        </Card>
        <div className={styles['layout']}>
          <Card
            title={t('admin.participant.pointAllocationTitle')}
            headerStyle={HeaderStyle.HEADER_5}
            buttonType={ButtonType.ICON}
            button={getButtonConfig.pointAllocation}
          >
            <div className={styles['allocation']}>
              {/* <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.orderNumber')}</div>
                <p>{data && data.purchase.length > 0 ? data.purchase.map((p) => p.orderNumber).join(', ') : '-'}</p>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.product')}</div>
                <p>BodyKey Start Pack</p>
              </div> */}
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.orderNumber')}</div>
                <div>
                  {data && data?.purchase?.length ? data?.purchase.map((p, i) => <p key={i}>{p.orderNumber}</p>) : '-'}
                </div>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.productName')}</div>
                <div>
                  {data && data?.purchase?.length ? data.purchase.map((p, i) => <p key={i}>{p.orderName}</p>) : '-'}
                </div>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.orderStatus')}</div>
                {data && data?.purchase?.length
                  ? data.purchase.map((p, i) => (
                      <p key={i}>{p.orderStatus === null ? '-' : p.orderStatus === 1 ? 'YES' : 'NO'}</p>
                    ))
                  : '-'}
              </div>
              <hr className={styles['horizontal-divider']} />
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.alpInvoiceNumber')}</div>
                <p>{data && data?.subscribe?.length > 0 ? data?.subscribe.map(p => p.orderNumber).join(', ') : '-'}</p>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.orderStatus')}</div>
                {data && data?.subscribe?.length ? (
                  data.subscribe.map((p, i) => (
                    <p key={i}>{p.orderStatus === null ? '-' : p.orderStatus === 1 ? 'YES' : 'NO'}</p>
                  ))
                ) : (
                  <p>-</p>
                )}
              </div>
              <hr className={styles['horizontal-divider']} />
              {/*<div className={styles['allocation-item']}>
                <div className={styles['label']}>Remarks</div>
                <p>{data?.point.remarks}</p>
              </div> */}
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.completed')}</div>
                <p>
                  {data && data?.isIndividualCompleted === null
                    ? '-'
                    : data?.isIndividualCompleted === 1
                      ? 'YES'
                      : 'NO'}
                </p>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.achieved')}</div>
                <p>
                  {data && data?.isIndividualAchieved === 1
                    ? 'YES'
                    : data?.isIndividualAchieved === 0
                      ? 'NO'
                      : 'PENDING'}
                </p>
              </div>
            </div>
          </Card>
          <Card
            title={t('admin.participant.rewardTitle')}
            headerStyle={HeaderStyle.HEADER_5}
            buttonType={ButtonType.ICON}
            button={getButtonConfig.rewardGift}
          >
            <TableContainer
              dataSource={
                data?.rewards?.filter(
                  item => item?.rewardStatus && !['REMOVED', 'REMOVED_BY_ADJUSTMENT'].includes(item?.rewardStatus),
                ) ?? []
              }
              getColumns={getRewardListConfig}
              pagination={false}
              rowKey="rewardRecId"
            />
          </Card>
        </div>
        <div className={styles['layout']}>
          <Card
            title={t('admin.participant.resultTitle')}
            headerStyle={HeaderStyle.HEADER_5}
            buttonType={ButtonType.ICON}
            button={getButtonConfig.result}
          >
            <div className={styles['result-container']}>
              <div className={styles['carousel-container']}>
                <ImageSlider
                  id="beforeImg"
                  slides={[
                    {
                      src: images.before_front.url ? images.before_front.url : PhotoUnavailable,
                      alt: 'Before Front View',
                    },
                    {
                      src: images.before_side.url ? images.before_side.url : PhotoUnavailable,
                      alt: 'Before Side View',
                    },
                  ]}
                  onClick={() => toggleEditModal(Sections.Result)}
                />
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.remark')}</div>
                  <p>{data?.weightIn?.remarks ?? '-'}</p>
                </div>
              </div>
              <div className={styles['result-info']}>
                <div className={styles['title']}>{t('admin.participant.before')}</div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.weighInDate')}</div>
                </div>
                <div className={styles['tag-calendar']}>
                  <div className={styles['tag-calendar']}>
                    <Tag
                      theme={Theme.SUCCESS}
                      text={
                        data?.weightIn?.weightedDate !== null
                          ? dateTimeToCustomFormat(data?.weightIn?.weightedDate ?? '', 'DD/MM/yyyy')
                          : '-'
                      }
                      style={TagStyle.OUTLINE}
                    />
                  </div>
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.machine')}</div>
                  <p>{data?.weightIn?.machine ?? '-'}</p>
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.height')}</div>
                  <p>{data?.weightIn?.height ?? '-'}</p>
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.weight')}</div>
                  <p>{data?.weightIn?.weight ?? '-'}</p>
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.muscleMassKg')}</div>
                  <p>{data?.weightIn?.muscleKilogram ?? '-'}</p>
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.muscleMassPercentage')}</div>
                  <p>{data?.weightIn?.musclePercentage ?? '-'}</p>
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.bodyFatPercentage')}</div>
                  <p>{data?.weightIn?.bodyFat ?? '-'}</p>
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.acknowledgedBy')}</div>
                  <p>{data?.weightIn?.acknowledgeByName ?? '-'}</p>
                </div>
                <div className={styles['label-container-ack']}>
                  <div className={styles['label']}>{t('admin.participant.ackowLedged')}</div>
                  <div className={styles['label-date']}>{t('admin.participant.acknowLedgedOfDate')}</div>
                  <p>
                    {data?.weightIn?.acknowledgeDateTime !== null && data?.weightIn?.acknowledgeDateTime !== undefined
                      ? dateTimeToCustomFormat(data?.weightIn?.acknowledgeDateTime ?? '', 'DD/MM/yyyy')
                      : '-'}
                  </p>
                </div>
              </div>
              <div className={styles['carousel-container']}>
                <ImageSlider
                  id="afterImg"
                  slides={[
                    {
                      src: images.after_front.url ? images.after_front.url : PhotoUnavailable,
                      alt: 'After Front View',
                    },
                    {
                      src: images.after_side.url ? images.after_side.url : PhotoUnavailable,
                      alt: 'Afer Side View',
                    },
                  ]}
                  onClick={() => toggleEditModal(Sections.Result)}
                />
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.remark')}</div>
                  <p>{data?.weightOut?.remarks ?? '-'}</p>
                </div>
              </div>
              <div className={styles['result-info']}>
                <div className={styles['title']}>{t('admin.participant.after')}</div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.weighOutDate')}</div>
                </div>
                <div className={styles['tag-calendar']}>
                  <Tag
                    theme={Theme.SUCCESS}
                    text={
                      data?.weightOut?.weightedDate !== null
                        ? dateTimeToCustomFormat(data?.weightOut?.weightedDate ?? '', 'DD/MM/yyyy')
                        : '-'
                    }
                    style={TagStyle.OUTLINE}
                  />
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.machine')}</div>
                  <p>{data?.weightOut?.machine ?? '-'}</p>
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.height')}</div>
                  <p>{data?.weightOut?.height ?? '-'}</p>
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.weight')}</div>
                  <p>{data?.weightOut?.weight ?? '-'}</p>
                  {data?.weightOutDiffValues?.weightDiff && data?.weightOut?.weightedDate ? (
                    <div
                      className={`${styles['difference-label']} ${
                        data?.weightOutDiffValues?.weightDiff > 0
                          ? styles.positive
                          : data?.weightOutDiffValues?.weightDiff === 0
                            ? styles.zero
                            : styles.negative
                      }`}
                    >
                      ({data?.weightOutDiffValues?.weightDiff > 0 ? '+' : ''}
                      {data?.weightOutDiffValues?.weightDiff?.toFixed(1) ?? '-'})
                    </div>
                  ) : null}
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.muscleMassKg')}</div>
                  <p>{data?.weightOut?.muscleKilogram ?? '-'}</p>
                  {data?.weightOutDiffValues?.muscleKilogramDiff && data?.weightOut?.weightedDate ? (
                    <div
                      className={`${styles['difference-label-alt']} ${
                        data?.weightOutDiffValues?.muscleKilogramDiff > 0
                          ? styles.positive
                          : data?.weightOutDiffValues?.muscleKilogramDiff === 0
                            ? styles.zero
                            : styles.negative
                      }`}
                    >
                      ({data?.weightOutDiffValues?.muscleKilogramDiff > 0 ? '+' : ''}
                      {data?.weightOutDiffValues?.muscleKilogramDiff?.toFixed(1) ?? '-'})
                    </div>
                  ) : null}
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.muscleMassPercentage')}</div>
                  <p>{data?.weightOut?.musclePercentage ?? '-'}</p>
                  {data?.weightOutDiffValues?.musclePercentageDiff && data?.weightOut?.weightedDate ? (
                    <div
                      className={`${styles['difference-label-alt']} ${
                        data?.weightOutDiffValues?.musclePercentageDiff > 0
                          ? styles.positive
                          : data?.weightOutDiffValues?.musclePercentageDiff === 0
                            ? styles.zero
                            : styles.negative
                      }`}
                    >
                      ({data?.weightOutDiffValues?.musclePercentageDiff > 0 ? '+' : ''}
                      {data?.weightOutDiffValues?.musclePercentageDiff?.toFixed(1) ?? '-'})
                    </div>
                  ) : null}
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.bodyFatPercentage')}</div>
                  <p>{data?.weightOut?.bodyFat ?? '-'}</p>
                  {data?.weightOutDiffValues?.bodyFatDiff && data?.weightOut?.weightedDate ? (
                    <div
                      className={`${styles['difference-label']} ${
                        data?.weightOutDiffValues?.bodyFatDiff > 0
                          ? styles.positive
                          : data?.weightOutDiffValues?.bodyFatDiff === 0
                            ? styles.zero
                            : styles.negative
                      }`}
                    >
                      ({data?.weightOutDiffValues?.bodyFatDiff > 0 ? '+' : ''}
                      {data?.weightOutDiffValues?.bodyFatDiff?.toFixed(1) ?? '-'})
                    </div>
                  ) : null}
                </div>
                <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.acknowledgedBy')}</div>
                  <p>{data?.weightOut?.acknowledgeByName ?? '-'}</p>
                </div>
                <div className={styles['label-container-ack']}>
                  <div className={styles['label']}>{t('admin.participant.ackowLedged')}</div>
                  <div className={styles['label-date']}>{t('admin.participant.acknowLedgedOfDate')}</div>
                  <p>
                    {data?.weightOut?.acknowledgeDateTime !== null && data?.weightOut?.acknowledgeDateTime !== undefined
                      ? dateTimeToCustomFormat(data?.weightOut?.acknowledgeDateTime ?? '', 'DD/MM/yyyy')
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
        <AuditHistoryAccordion auditHistoryLog={auditHistoryLog} displayAuditEmpty={auditHistoryLog?.length <= 0} />
      </div>
      {showModal}
    </div>
  );
};
